import { FunctionComponent } from 'react'

import styled, { css } from 'styled-components'

import { desktopCSS } from '@measures/responsive'
import useVideoContext from '@hooks/useVideoContext'

import { GraphicsObject } from '../../types'
import { splitOnLines } from '../../utils/string'

export type GraphicsElementsProps = {
  graphicsObject: GraphicsObject
}

type MainElementProps = {
  isMainElement: boolean
}

const GraphicsWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing4 },
    },
  }) => {
    return css`
      margin-top: ${spacing4};
      width: 100%;
    `
  }}
`

const TextWrapper = styled.div<MainElementProps>`
  ${({
    theme: {
      spacing: { spacing4 },
    },
    isMainElement,
  }) => {
    return css`
      text-align: center;
      margin-top: 0;

      ${desktopCSS(css`
        margin-top: ${isMainElement ? '9px' : spacing4};
      `)}
    `
  }}
`

const GraphicsHeadline = styled.div<MainElementProps>`
  ${({
    theme: {
      typography: {
        headings: {
          xsmall: { bundledCSS: xsmallHeadingCSS },
        },
      },
      colors: { staticRed500, staticWhite },
      spacing: { spacing2, spacing4 },
    },
    isMainElement,
  }) => css`
    ${xsmallHeadingCSS};
    font-size: 3cqw;

    display: inline;
    box-decoration-break: clone;

    background-color: ${staticWhite};
    color: ${staticRed500};

    padding: 0 ${spacing2};
    ${desktopCSS(css`
      padding: ${isMainElement ? '0 6px' : `0 ${spacing4}`};
      .jw-flag-fullscreen & {
        padding: 0 6px;
      }
    `)}
  `}
`

const GraphicsName = styled.div<MainElementProps>`
  ${({
    theme: {
      typography: {
        headings: {
          medium: { bundledCSS: mediumHeadingCSS },
        },
      },
      colors: { staticWhite, staticRed500 },
      spacing: { spacing4, spacing12 },
    },
    isMainElement,
  }) => css`
    ${mediumHeadingCSS};
    font-size: 4cqw;
    padding: 0 ${spacing4};

    display: inline;
    box-decoration-break: clone;

    color: ${staticRed500};
    background-color: ${staticWhite};
    text-transform: uppercase;

    ${desktopCSS(css`
      padding: ${isMainElement ? `${spacing4} ${spacing12}` : '0 6px'};
      .jw-flag-fullscreen & {
        padding: ${spacing4} ${spacing12};
      }
    `)}
  `}
`

const GraphicsSource = styled.div<MainElementProps>`
  ${({
    theme: {
      typography: {
        headings: {
          xsmall: { bundledCSS: xsmallHeadingCSS },
        },
      },
      colors: { staticRed500, staticWhite },
      spacing: { spacing2, spacing4 },
    },
    isMainElement,
  }) => css`
    ${xsmallHeadingCSS};
    font-size: 3cqw;

    display: inline;
    box-decoration-break: clone;

    background-color: ${staticRed500};
    color: ${staticWhite};

    padding: 0 ${spacing2};
    ${desktopCSS(css`
      padding: ${isMainElement ? '0 6px' : `0 ${spacing4}`};
      .jw-flag-fullscreen & {
        padding: 0 6px;
      }
    `)}
  `}
`

const GraphicsFunction = GraphicsSource

const GraphicsElements: FunctionComponent<GraphicsElementsProps> = ({
  graphicsObject,
}) => {
  const { isMainElement } = useVideoContext()
  return Object.keys(graphicsObject).length ? (
    <GraphicsWrapper>
      {graphicsObject.headline ? (
        <TextWrapper isMainElement={isMainElement}>
          {splitOnLines(graphicsObject.headline).map((text, index) =>
            text ? (
              <GraphicsHeadline key={index} isMainElement={isMainElement}>
                {text}
              </GraphicsHeadline>
            ) : null,
          )}
        </TextWrapper>
      ) : null}
      {graphicsObject.name ? (
        <TextWrapper isMainElement={isMainElement}>
          {splitOnLines(graphicsObject.name).map((text, index) =>
            text ? (
              <GraphicsName key={index} isMainElement={isMainElement}>
                {text}
              </GraphicsName>
            ) : null,
          )}
        </TextWrapper>
      ) : null}
      {graphicsObject.source ? (
        <TextWrapper isMainElement={isMainElement}>
          {splitOnLines(graphicsObject.source).map((text, index) =>
            text ? (
              <GraphicsSource key={index} isMainElement={isMainElement}>
                {text}
              </GraphicsSource>
            ) : null,
          )}
        </TextWrapper>
      ) : null}
      {graphicsObject.function ? (
        <TextWrapper isMainElement={isMainElement}>
          {splitOnLines(graphicsObject.function).map((text, index) =>
            text ? (
              <GraphicsFunction key={index} isMainElement={isMainElement}>
                {text}
              </GraphicsFunction>
            ) : null,
          )}
        </TextWrapper>
      ) : null}
    </GraphicsWrapper>
  ) : null
}

export default GraphicsElements
